/**
 * Discord REST client for posting roll results to Discord channels
 *
 * Unlike WebSocketClient, Discord uses REST calls through the backend proxy.
 * The backend owns the bot token; this client just posts roll results.
 */

import { createScopedReporter } from './errorReporter';
import { API_BASE_URL, API_KEY } from '@/config/api';

const SERVICE_TAG = '[DISCORD_CLIENT]';
const reporter = createScopedReporter('DISCORD');

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

interface DiscordClientConfig {
  onStatusChange?: (status: ConnectionStatus) => void;
  onError?: (message: { type: 'error'; message: string; code?: string }) => void;
}

interface DiscordConnectionParams {
  channelId: string;
  guildId: string;
}

/**
 * Roll result payload for Discord post-result endpoint
 */
interface DiscordRollResult {
  requestId: string;
  playerId: string;
  results: Array<{
    type: string;
    value: number;
    wasEdited: boolean;
    confidence: number;
    originalValue?: number;
  }>;
}

/**
 * REST-based client for posting dice roll results to Discord
 *
 * Implements the same lifecycle interface as WebSocketClient so that
 * ConnectionManager can handle both types uniformly.
 */
export class DiscordClient {
  private config: DiscordClientConfig = {};
  private endpointId: string;
  private status: ConnectionStatus = 'disconnected';
  private params: DiscordConnectionParams | null = null;

  constructor(endpointId: string) {
    this.endpointId = endpointId;
    console.info(`${SERVICE_TAG} Created Discord client`, { endpointId });
  }

  /**
   * Configure event handlers
   */
  configure(config: DiscordClientConfig): void {
    this.config = config;
  }

  /**
   * Connect to Discord (verifies configuration, sets status)
   * Discord is REST-based so there is no persistent connection to establish.
   */
  async connect(params: DiscordConnectionParams): Promise<void> {
    this.params = params;
    this.updateStatus('connecting');

    if (!params.channelId || !params.guildId) {
      this.updateStatus('disconnected');
      const errorMsg = 'Missing channel or guild ID';
      this.config.onError?.({ type: 'error', message: errorMsg });
      throw new Error(errorMsg);
    }

    // Discord is always "connected" since we use REST calls
    this.updateStatus('connected');
    console.info(`${SERVICE_TAG} Discord client ready`, {
      channelId: params.channelId,
      endpointId: this.endpointId
    });
  }

  /**
   * Disconnect from Discord (clears state)
   */
  disconnect(): void {
    console.info(`${SERVICE_TAG} Disconnecting Discord client`, {
      endpointId: this.endpointId
    });
    this.params = null;
    this.updateStatus('disconnected');
  }

  /**
   * Check if Discord client is ready to send
   */
  isConnected(): boolean {
    return this.status === 'connected' && this.params !== null;
  }

  /**
   * Get the endpoint ID this client belongs to
   */
  getEndpointId(): string {
    return this.endpointId;
  }

  /**
   * Post a roll result to Discord via the backend proxy
   *
   * When `imageData` is provided, the backend attaches it as a file to the Discord
   * message and renders it as a large inline image in the embed. When `videoData`
   * is provided (TICKET-281f), the backend attaches the video as a playable file
   * card below the embed; video wins over image when both are passed.
   *
   * @param rollResult - The roll result to post
   * @param options - Display options (formula, player name, base64 image/video data)
   */
  async sendRollResult(
    rollResult: DiscordRollResult,
    options?: {
      formula?: string;
      playerName?: string;
      imageData?: string;
      /** Base64-encoded video bytes (with or without data-URI prefix). PWA-direct
       *  Discord path; mirrors imageData. (TICKET-281f) */
      videoData?: string;
      /** MIME of the video — passed through to backend so it can choose the
       *  attachment filename and content-type. (TICKET-281f) */
      contentType?: 'video/webm' | 'video/mp4';
    }
  ): Promise<void> {
    if (!this.params) {
      throw new Error('Cannot send roll result: Discord client is not connected');
    }

    const url = `${API_BASE_URL}/discord/post-result`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': API_KEY
        },
        body: JSON.stringify({
          channelId: this.params.channelId,
          rollResult,
          formula: options?.formula,
          playerName: options?.playerName,
          imageData: options?.imageData,
          videoData: options?.videoData,
          contentType: options?.contentType
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ message: 'Unknown error' }));
        reporter.error('Discord post failed', {
          technicalDetails: `POST /discord/post-result returned ${response.status}`,
          context: { status: response.status },
          showToast: false
        });
        throw new Error(errorBody.message || `Discord API error: ${response.status}`);
      }

      console.info(`${SERVICE_TAG} Roll result posted to Discord`, {
        requestId: rollResult.requestId,
        channelId: this.params.channelId
      });
    } catch (error) {
      console.error(`${SERVICE_TAG} Failed to post roll result:`, {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      this.config.onError?.({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to post to Discord'
      });
      throw error;
    }
  }

  /**
   * Update connection status and notify listener
   */
  private updateStatus(status: ConnectionStatus): void {
    this.status = status;
    this.config.onStatusChange?.(status);
  }
}
